import { type ThunkAction } from 'redux-thunk';
import type { HttpClient } from '@wix/http-client';
import { updateDraftPost } from '@wix/ambassador-blog-v3-draft/http';
import { Action, Field } from '@wix/ambassador-blog-v3-draft/types';
import type { ErrorHandlerPublicAPI } from '@wix/yoshi-flow-editor/error-handler';
import {
  PUB_SUB_CHANNEL_APP,
  PUB_SUB_CHANNEL_MODAL,
  createAction,
  getIsDebugEnabled,
  getCustomPostUrl,
} from '@wix/communities-blog-client-common';
// @ts-expect-error
import { isPublished } from '@wix/communities-blog-universal/dist/src/post-utils';
import { createPromisifiedAction } from '../actions-promisifier/create-promisified-action';
import { getLatestSlug } from '../helpers/get-latest-slug';
import { getPost } from '../selectors/post-selectors';
import { debug } from '../services/debug';
import { normalizePost } from '../services/post-utils';
import { getCategoryIds } from '../store/categories/categories-selectors';
import { setIsLoading } from '../store/is-loading/is-loading-actions';
import { type AppState } from '../types';
import { navigateToCustomPostUrl } from './navigate-within-blog';
import {
  PUBLISH_POST_FAILURE,
  PUBLISH_POST_REQUEST,
  PUBLISH_POST_SUCCESS,
} from './publish-post-types';

const publishPostRequest = createAction(PUBLISH_POST_REQUEST);
const publishPostSuccess = createAction(
  PUBLISH_POST_SUCCESS,
  (payload, meta) => payload,
  (payload, meta) => meta,
);
const publishPostFailure = createAction(PUBLISH_POST_FAILURE);

const setIsPublishing = (isPublishing: boolean) => ({
  ...setIsLoading('publish', undefined, isPublishing),
  meta: { publish: [PUB_SUB_CHANNEL_APP, PUB_SUB_CHANNEL_MODAL] },
});

type PublishPostThunk = ThunkAction<
  Promise<any>,
  AppState,
  { httpClient: HttpClient; errorHandler: ErrorHandlerPublicAPI },
  any
>;

const publishPost =
  (postId: string): PublishPostThunk =>
  async (dispatch, getState, { httpClient, errorHandler }) => {
    dispatch(publishPostRequest());
    dispatch(setIsPublishing(true));

    const state = getState();
    const isUpdated = isPublished(getPost(state, postId));
    const blogCategoryIds = getCategoryIds(state);
    const { withErrorHandler, getResolvedError } = errorHandler;

    const updateDraftPostPromise = withErrorHandler(
      () =>
        httpClient
          .request(
            updateDraftPost({
              draftPost: { id: postId },
              action: Action.UPDATE_PUBLISH,
              fieldsets: [Field.URL],
            }),
          )
          .then(({ data }) => {
            const post = data.draftPost;

            if (!post) {
              throw new Error('Post not found');
            }

            const slugs = post.slugs;
            return {
              ...post,
              slug: getLatestSlug(slugs),
            };
          }),
      { errorCodesMap: {} },
    );

    try {
      const post = await updateDraftPostPromise;
      dispatch(
        publishPostSuccess(normalizePost({ post, blogCategoryIds }), {
          isUpdated,
        }),
      );
    } catch (error) {
      getResolvedError(error);
      getIsDebugEnabled(state) && debug.error(error);
      dispatch(publishPostFailure(error));
    }
    dispatch(setIsPublishing(false));
    return updateDraftPostPromise;
  };

type PublishPostAndNavigateThunk = ThunkAction<void, AppState, any, any>;

export const publishPostAndNavigate =
  (postId: string): PublishPostAndNavigateThunk =>
  (dispatch, getState) =>
    // @ts-expect-error
    dispatch(publishPost(postId)).then(({ slug }) =>
      dispatch(navigateToCustomPostUrl(getCustomPostUrl(getState(), slug), `/${slug}`)),
    );

export const publishPostPromisified = createPromisifiedAction(
  publishPost,
  (data) => data,
  (response) => response.status,
);
